// Phase 1 — Sidebar navigation model. See docs/02-information-architecture.md.
//
// This is presentation-only chrome: every `to` is an EXISTING route from
// src/Navigates/Navigates.tsx and every label reuses the existing AppConstant
// strings. The groups/items mirror the legacy horizontal menu (apparelMenu.tsx)
// one-to-one so nothing a user could reach before disappears.

import {
  Package,
  RefreshCw,
  Truck,
  RotateCcw,
  ShoppingCart,
  FileText,
  Globe,
  Users,
  User,
  Store,
  Receipt,
  BarChart3,
  Settings,
  Tag,
  Image,
  Flame,
  Star,
  History,
  CalendarCheck,
  ScrollText,
  PartyPopper,
  MessageSquareText,
  Building2,
  type LucideIcon,
} from "lucide-react";
import AppConstant from "../Globals/AppConstants";

export type NavItem = { label: string; to: string; icon: LucideIcon };
export type NavGroup = { id: string; label: string; icon: LucideIcon; items: NavItem[] };

// Mirrors the legacy ApparelMenu SubMenus (keys 1–13) exactly.
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "inventory",
    label: AppConstant.menuHeader.inventory,
    icon: Package,
    items: [
      { label: AppConstant.menus.stock, to: "/inventoryManagement", icon: Package },
      { label: "Stock Ledger", to: "/stock/history", icon: History },
      // { label: AppConstant.menus.cycle, to: "/cycle/count", icon: RefreshCw },
      { label: AppConstant.menus.procurement, to: "/procurement", icon: Truck },
    ],
  },
  {
    id: "orders",
    label: AppConstant.menuHeader.orders,
    icon: ShoppingCart,
    items: [
      { label: AppConstant.menus.bill, to: "/billing", icon: FileText },
      { label: AppConstant.menus.salesReturn, to: "/salesReturn", icon: RotateCcw },
      { label: AppConstant.menus.vendorPurchase, to: "/vendor-purchase", icon: Receipt },
      { label: AppConstant.menus.rental, to: "/rental", icon: Building2 },
      // { label: AppConstant.menus.onlineBilling, to: "/orders/online-billing", icon: Globe },
    ],
  },
  {
    id: "users",
    label: AppConstant.menuHeader.users,
    icon: Users,
    items: [
      { label: AppConstant.menus.customer, to: "/customer", icon: User },
      { label: AppConstant.menus.vendor, to: "/vendor", icon: Store },
      { label: AppConstant.menus.feedbackList, to: "/feedback/list", icon: MessageSquareText },
    ],
  },
  {
    id: "reports",
    label: AppConstant.menus.report,
    icon: BarChart3,
    items: [
      { label: AppConstant.menus.dashboard, to: "/report/dashboard", icon: BarChart3 },
      { label: AppConstant.menus.salesDashboard, to: "/report/sales-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.inventoryDashboard, to: "/report/inventory-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.customerDashboard, to: "/report/customer-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.vendorDashboard, to: "/report/vendor-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.employeeDashboard, to: "/report/employee-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.customDashboard, to: "/report/custom-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.customerCreditDashboard, to: "/report/customer-credit-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.vendorDebtDashboard, to: "/report/vendor-debt-dashboard", icon: BarChart3 },
      { label: AppConstant.menus.feedbackDashboard, to: "/report/feedback-dashboard", icon: BarChart3 },
    ],
  },
  {
    id: "admin",
    label: AppConstant.menuHeader.admin,
    icon: Settings,
    items: [
      { label: AppConstant.menus.category, to: "/mugizh-admin/category", icon: Tag },
      { label: AppConstant.menus.employee, to: "/mugizh-admin/employee", icon: Users },
      { label: AppConstant.menus.auditLog, to: "/mugizh-admin/audit-log", icon: ScrollText },
      { label: AppConstant.menus.festival, to: "/mugizh-admin/festival", icon: PartyPopper },
      { label: AppConstant.menus.attendance, to: "/attendance", icon: CalendarCheck },
      // { label: AppConstant.menus.banners, to: "/mugizh-admin/banner", icon: Image },
      // { label: AppConstant.HOTNEW, to: "/mugizh-admin/hot-new", icon: Flame },
      // { label: AppConstant.TOPPICK, to: "/mugizh-admin/top-picks", icon: Star },
    ],
  },
];

// Route -> breadcrumb metadata. Covers linked items plus reachable sub-routes
// (create/detail screens) so the topbar always has a sensible label.
export const ROUTE_META: Record<string, { group?: string; label: string }> = {
  "/inventoryManagement": { group: AppConstant.menuHeader.inventory, label: AppConstant.menus.stock },
  "/stock/history": { group: AppConstant.menuHeader.inventory, label: "Stock Ledger" },
  "/inventoryManagement/stock-history": { group: AppConstant.menuHeader.inventory, label: "Stock History" },
  "/cycle/count": { group: AppConstant.menuHeader.inventory, label: AppConstant.menus.cycle },
  "/cycle/singleandmultiple": { group: AppConstant.menuHeader.inventory, label: AppConstant.menus.cycle },
  "/procurement": { group: AppConstant.menuHeader.inventory, label: AppConstant.menus.procurement },
  "/salesReturn": { group: AppConstant.menuHeader.orders, label: AppConstant.menus.salesReturn },
  "/barcode/list": { group: AppConstant.menuHeader.inventory, label: "Barcode List" },
  "/billing": { group: AppConstant.menuHeader.orders, label: AppConstant.menus.bill },
  "/create/bill": { group: AppConstant.menuHeader.orders, label: "Create Bill" },
  "/orders/online-billing": { group: AppConstant.menuHeader.orders, label: AppConstant.menus.onlineBilling },
  "/customer": { group: AppConstant.menuHeader.users, label: AppConstant.menus.customer },
  "/vendor": { group: AppConstant.menuHeader.users, label: AppConstant.menus.vendor },
  "/vendor/create": { group: AppConstant.menuHeader.users, label: "Create Vendor" },
  "/vendor-purchase": { group: AppConstant.menuHeader.orders, label: AppConstant.menus.vendorPurchase },
  "/report/list": { group: AppConstant.menus.report, label: AppConstant.menus.report },
  "/report/dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.dashboard },
  "/report/sales-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.salesDashboard },
  "/report/inventory-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.inventoryDashboard },
  "/report/customer-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.customerDashboard },
  "/report/vendor-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.vendorDashboard },
  "/report/employee-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.employeeDashboard },
  "/report/custom-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.customDashboard },
  "/report/customer-credit-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.customerCreditDashboard },
  "/report/vendor-debt-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.vendorDebtDashboard },
  "/report/feedback-dashboard": { group: AppConstant.menus.report, label: AppConstant.menus.feedbackDashboard },
  "/feedback/list": { group: AppConstant.menuHeader.users, label: AppConstant.menus.feedbackList },
  "/sales/report": { group: AppConstant.menus.report, label: "Sales Report" },
  "/reports/sales": { group: AppConstant.menus.report, label: "Sales Report" },
  "/mugizh-admin/category": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.category },
  "/mugizh-admin/banner": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.banners },
  "/mugizh-admin/hot-new": { group: AppConstant.menuHeader.admin, label: AppConstant.HOTNEW },
  "/mugizh-admin/top-picks": { group: AppConstant.menuHeader.admin, label: AppConstant.TOPPICK },
  "/mugizh-admin/dashboard": { group: AppConstant.menuHeader.admin, label: "Dashboard" },
  "/mugizh-admin/my-orders-list": { group: AppConstant.menuHeader.admin, label: "My Orders" },
  "/mugizh-admin/customer": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.customer },
  "/mugizh-admin/vendor": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.vendor },
  "/mugizh-admin/employee": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.employee },
  "/attendance": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.attendance },
  "/rental": { group: AppConstant.menuHeader.orders, label: AppConstant.menus.rental },
  "/mugizh-admin/reports": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.report },
  "/mugizh-admin/audit-log": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.auditLog },
  "/mugizh-admin/festival": { group: AppConstant.menuHeader.admin, label: AppConstant.menus.festival },
};

export function getBreadcrumb(pathname: string): { group?: string; label: string } {
  return ROUTE_META[pathname] || { label: "" };
}
